// Standalone Nav + Footer chrome extracted from _v08_cinematic.jsx so
// they can be mounted on every interior page of the portal — not just
// the homepage. Visual treatment is byte-identical to the variant, with
// all dynamic copy bindings (t/ts/useDomainCopy) collapsed to their
// default strings. The only configurable input is `brand`.
//
// Notes:
// - The original variant nav lives INSIDE the hero <section> on top of
//   a layered backdrop. Extracted here as a standalone block (without
//   the hero backdrop) so it can be rendered on its own. The visual
//   treatment of the nav row itself is byte-identical.
// - The 3-column brand split (main / accent / suffix) collapses to a
//   single `brand` string when only `brand` is supplied (matching the
//   variant's behaviour when admins haven't overridden the slots).
import React, { useState, useEffect } from 'react';
import { Link } from '../_shared.jsx';

const theme = {
  bg: '#0d1117', fg: '#f3f0ea', muted: '#9fa3aa',
  border: 'rgba(255,255,255,0.14)', accent: '#f3c969', accentFg: '#0d1117',
  inputBg: 'rgba(255,255,255,0.06)', radius: 8,
  font: '"Inter", system-ui, sans-serif',
  serif: '"Playfair Display", "Source Serif 4", Georgia, serif',
  mono: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
  bgDarker: '#0a0e15',
  cream: '#f7f7f0',
  creamMuted: 'rgba(247,247,240,0.65)',
  goldRule: 'rgba(243,201,105,0.18)',
  goldRuleFaint: 'rgba(243,201,105,0.15)',
  goldGlassBorder: 'rgba(243,201,105,0.20)',
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update); };
  }, []);
  return isMobile;
};

const HamburgerIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> :
            <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
  </svg>
);

const navLinks = [
  { label: 'Anlagen', href: '/produkte' },
  { label: 'Solarrechner', href: '/solarrechner' },
  { label: 'Wärmepumpe', href: '/wärmepumpe' },
  { label: 'Förderung', href: '/förderung-finder' },
  { label: 'Magazin', href: '/ratgeber' },
];

const footerColumns = [
  {
    header: 'ANLAGEN',
    links: [
      { label: 'Photovoltaik', href: '/photovoltaik' },
      { label: 'Komplett­anlagen', href: '/produkte' },
    ],
  },
  {
    header: 'TOOLS',
    links: [
      { label: 'Solarrechner', href: '/solarrechner' },
      { label: 'Wärmepumpen-Rechner', href: '/wärmepumpen-rechner' },
    ],
  },
  {
    header: 'WÄRMEPUMPE',
    links: [
      { label: 'Wärmepumpe', href: '/wärmepumpe' },
      { label: 'Glossar', href: '/glossar' },
    ],
  },
  {
    header: 'FÖRDERUNG',
    links: [
      { label: 'Förderfinder', href: '/förderung-finder' },
      { label: 'Marktanalyse', href: '/marktanalyse' },
    ],
  },
];

// `transparent` lets the homepage variant render this on top of the hero's
// absolute-positioned backdrop without obscuring it. Interior pages omit
// the prop and get the solid dark background + border for legibility.
export function V08CinematicNav({ brand = 'SolarExperten24', transparent = false, brandLogo = null }) {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const hasBrand = !!brand;
  const brandMain = hasBrand ? brand : 'Solar';
  const brandAccent = hasBrand ? '' : 'Experten';
  const brandSuffix = hasBrand ? '' : '24';
  const brandLabel = brand || 'SolarExperten24';
  return (
    <div style={{
      background: transparent ? 'transparent' : theme.bg,
      color: theme.fg,
      fontFamily: theme.font,
      padding: isMobile ? '14px 16px' : '20px 40px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      borderBottom: transparent ? 'none' : `1px solid ${theme.border}`,
      position: 'relative', zIndex: 2,
    }}>
      <Link href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: 'inherit' }} aria-label={brandLabel + ' Startseite'}>
        {brandLogo
          ? <img src={brandLogo} alt={brandLabel + ' Logo'} width="120" height="32" style={{ height: 32, width: 'auto', maxWidth: 160, display: 'block' }} />
          : <>
              <div style={{ width: 24, height: 24, borderRadius: 4, background: theme.accent }}/>
              <div style={{ fontWeight: 700, fontSize: isMobile ? 15 : 17, letterSpacing: '-0.01em' }}>{brandMain}<span style={{ fontWeight: 300, opacity: 0.7 }}>{brandAccent}</span><span style={{ color: theme.accent }}>{brandSuffix}</span></div>
            </>
        }
      </Link>
      {!isMobile && (
        <>
          <div style={{ display: 'flex', gap: 26, fontSize: 13, color: theme.muted }}>
            {navLinks.map(l => <Link key={l.label} href={l.href} style={{ color: theme.muted, textDecoration: 'none' }}>{l.label}</Link>)}
          </div>
          <Link href="/angebot-vergleichen" style={{
            padding: '9px 18px', fontSize: 13, fontWeight: 600, fontFamily: theme.font,
            background: theme.accent, color: theme.accentFg, border: 'none', borderRadius: 999, cursor: 'pointer',
            textDecoration: 'none', display: 'inline-block',
          }}>{'Anfrage starten'}</Link>
        </>
      )}
      {isMobile && (
        <button
          aria-label="Menü"
          onClick={() => setIsMenuOpen(o => !o)}
          style={{ background: 'transparent', border: 'none', color: theme.fg, cursor: 'pointer', padding: 4, display: 'flex' }}
        >
          <HamburgerIcon open={isMenuOpen} />
        </button>
      )}
      {isMobile && isMenuOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: theme.bg, borderBottom: `1px solid ${theme.border}`,
          padding: '16px', display: 'flex', flexDirection: 'column', gap: 14,
          zIndex: 50,
        }}>
          {navLinks.map(l => (
            <Link key={l.label} href={l.href} style={{ color: theme.fg, textDecoration: 'none', fontSize: 15 }}>{l.label}</Link>
          ))}
          <Link href="/angebot-vergleichen" style={{
            padding: '10px 18px', fontSize: 14, fontWeight: 600,
            background: theme.accent, color: theme.accentFg,
            border: 'none', borderRadius: 999,
            textAlign: 'center', textDecoration: 'none', display: 'block', marginTop: 4,
          }}>{'Anfrage starten'}</Link>
        </div>
      )}
    </div>
  );
}

export function V08CinematicFooter({ brand = 'SolarExperten24', domain = null, brandLogo = null }) {
  const isMobile = useIsMobile();
  const hasBrand = !!brand;
  const brandMain = hasBrand ? brand : 'Solar';
  const brandAccent = hasBrand ? '' : 'Experten';
  const brandSuffix = hasBrand ? '' : '24';
  const brandLabel = brand || 'SolarExperten24';
  const contactEmail = `info@${domain?.name || (brand ? brand.toLowerCase() + '.de' : 'scienceworks.com')}`;
  return (
    <footer style={{
      background: theme.bg,
      borderTop: `1px solid ${theme.goldRule}`,
      padding: isMobile ? '28px 16px 18px' : '48px 56px 28px',
      color: theme.cream,
      fontFamily: theme.font,
    }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1.2fr 2.6fr 1fr',
          gap: isMobile ? 28 : 48,
          alignItems: 'start',
          marginBottom: isMobile ? 24 : 40,
        }}>
          {/* Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              {brandLogo
                ? <img src={brandLogo} alt={brandLabel + ' Logo'} width="120" height="32" style={{ height: 32, width: 'auto', maxWidth: 160, display: 'block' }} />
                : <>
                    <div style={{ width: 24, height: 24, borderRadius: 4, background: theme.accent }}/>
                    <div style={{ fontWeight: 700, fontSize: 17, letterSpacing: '-0.01em' }}>
                      {brandMain}<span style={{ fontWeight: 300, opacity: 0.7 }}>{brandAccent}</span><span style={{ color: theme.accent }}>{brandSuffix}</span>
                    </div>
                  </>
              }
            </div>
            <p style={{
              fontSize: 13, lineHeight: 1.65,
              color: theme.creamMuted,
              margin: 0, maxWidth: 260,
            }}>
              {'Premium-Solartechnik aus deutscher Hand. Geplant von Ingenieuren, gebaut von Fachbetrieben.'}
            </p>
          </div>

          {/* 4 nav columns */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: isMobile ? 20 : 32,
          }}>
            {footerColumns.map(col => (
              <div key={col.header}>
                <div style={{
                  fontFamily: theme.mono,
                  fontSize: 10.5,
                  letterSpacing: '0.20em',
                  color: theme.accent,
                  fontWeight: 600,
                  marginBottom: 16,
                }}>{col.header}</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {col.links.map(l => (
                    <li key={l.label} style={{ fontSize: 13, lineHeight: 1.4 }}>
                      <Link href={l.href} style={{ color: theme.cream, textDecoration: 'none' }}>{l.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
            <Link href="/angebot-vergleichen" style={{
              padding: '9px 18px', fontSize: 13, fontWeight: 600, fontFamily: theme.font,
              background: theme.accent, color: theme.accentFg,
              border: 'none', borderRadius: 999, cursor: 'pointer',
              letterSpacing: '0.01em',
              textDecoration: 'none', display: 'inline-block',
            }}>{'Anfrage starten'}</Link>
            <div style={{
              marginTop: 18,
              fontFamily: theme.mono, fontSize: 11,
              letterSpacing: '0.18em', textTransform: 'uppercase',
              color: theme.creamMuted, lineHeight: 1.7,
            }}>
              {'ANFRAGEN PER FORMULAR · 24/7'}<br/>
              <a href={`mailto:${contactEmail}`} style={{ color: theme.cream, textDecoration: 'none' }}>{contactEmail.toUpperCase()}</a>
            </div>
          </div>
        </div>

        {/* gold rule */}
        <div style={{ height: 2, background: theme.accent, opacity: 0.85, marginBottom: 18 }}/>

        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexWrap: 'wrap', gap: isMobile ? 10 : 16,
          fontFamily: theme.mono,
          fontSize: isMobile ? 10 : 11,
          letterSpacing: '0.16em', textTransform: 'uppercase',
          color: theme.creamMuted,
        }}>
          <div>{`© ${new Date().getFullYear()} ${brandLabel}`}</div>
          <div style={{ display: 'flex', gap: isMobile ? 14 : 22, flexWrap: 'wrap' }}>
            <Link href="/impressum" style={{ color: theme.creamMuted, textDecoration: 'none' }}>{'Impressum'}</Link>
            <Link href="/datenschutz" style={{ color: theme.creamMuted, textDecoration: 'none' }}>{'Datenschutz'}</Link>
            <Link href="/agb" style={{ color: theme.creamMuted, textDecoration: 'none' }}>{'AGB'}</Link>
            <Link href="/widerruf" style={{ color: theme.creamMuted, textDecoration: 'none' }}>{'Widerruf'}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
